import { c as _c } from "react/compiler-runtime";
import { Box } from "@chakra-ui/react";
import { DateTime, Interval } from "luxon";
import { useCallback, useEffect, useMemo, useState } from "react";
import { MainLayout } from "@/components/layout/MainLayout";
import { MapMain } from "@/components/map/MapMain";
import { Menu, ViewType, FilterType, filtersByView } from "@/components/ui/Menu";
import { TrajectoryPointDtoVehicleType } from "@/api/generated/models/trajectoryPointDtoVehicleType";
import { VehicleTypeFilter } from "@/components/ui/filters/vehicleType/VehicleTypeFilter";
import { TimeFilter } from "@/components/ui/filters/timePicker/TimeFilter";
import { clampToIntervals, dateToMinutes, getDefaultDayIntervalsSelection, isValidInterval } from "@/components/ui/filters/timePicker/timePickerUtils";
import { MetricFilter, RangeMetric, RangeValues } from "@/components/ui/filters/metrics/MetricFilter";
import { useMetricRanges, useTimeIntervals, useVehicleTypes } from "@/api/hooks/useStats";
import { ToggleMetric, ViewMode, METRIC_VIEW_AVAILABILITY, getDefaultViewMode } from "@/utils/metricUtils";
import { Metric } from "@/api/generated/models";
import { TimeIntervalDisplay } from "@/components/ui/filters/timePicker/TimeIntervalDisplay";
export interface MapMainProps {
  displayedVehicleTypes: TrajectoryPointDtoVehicleType[];
  selectedMetric: ToggleMetric;
  viewMode: ViewMode;
  selectedStartTime: DateTime | null;
  selectedEndTime: DateTime | null;
  rangeValues: RangeValues | null;
  activeView: ViewType;
}
export const BaseMap = () => {
  const $ = _c(61);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = Object.values(TrajectoryPointDtoVehicleType);
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [displayedVehicleTypes, setDisplayedVehicleTypes] = useState(t0);
  const [selectedMetric, setSelectedMetric] = useState(Metric.vehicleType);
  const [viewMode, setViewMode] = useState(ViewMode.trajectories);
  const {
    data: metricRanges
  } = useMetricRanges();
  const {
    data: vehicleTypesData
  } = useVehicleTypes();
  const {
    data: timeData
  } = useTimeIntervals();
  let t1;
  if ($[1] !== metricRanges) {
    t1 = metricRanges?.speed && metricRanges?.acceleration && metricRanges?.density ? {
      speed: metricRanges.speed.absolute,
      acceleration: metricRanges.acceleration.absolute,
      density: metricRanges.density.absolute
    } : null;
    $[1] = metricRanges;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const datasetRanges = t1;
  const [rangeValues, setRangeValues] = useState(null);
  let t2;
  let t3;
  if ($[3] !== metricRanges || $[4] !== rangeValues) {
    t2 = () => {
      if (!metricRanges || rangeValues) {
        return;
      }
      if (!metricRanges.speed || !metricRanges.acceleration || !metricRanges.density) {
        return;
      }
      setRangeValues({
        speed: metricRanges.speed.percentile,
        acceleration: metricRanges.acceleration.percentile,
        density: metricRanges.density.percentile
      });
    };
    t3 = [metricRanges, rangeValues];
    $[3] = metricRanges;
    $[4] = rangeValues;
    $[5] = t2;
    $[6] = t3;
  } else {
    t2 = $[5];
    t3 = $[6];
  }
  useEffect(t2, t3);
  let t4;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = (metric, range) => {
      setRangeValues(prev => prev ? {
        ...prev,
        [metric]: range
      } : prev);
    };
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const handleRangeChange = t4;
  const [selectedStartTimestamp, setSelectedStartTimestamp] = useState(null);
  const [selectedEndTimestamp, setSelectedEndTimestamp] = useState(null);
  const [draftStartTimestamp, setDraftStartTimestamp] = useState(null);
  const [draftEndTimestamp, setDraftEndTimestamp] = useState(null);
  let t5;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = (start, end) => {
      setDraftStartTimestamp(start);
      setDraftEndTimestamp(end);
    };
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const handleDraftChange = t5;
  const [activeView, setActiveView] = useState(ViewType.trafficAnalysis);
  const [activeFilter, setActiveFilter] = useState(null);
  let t6;
  let t7;
  if ($[9] !== timeData) {
    t6 = () => {
      if (!timeData?.timeIntervals?.length) {
        return;
      }
      const bounds = getDefaultDayIntervalsSelection(timeData.timeIntervals.filter(isValidInterval));
      if (!bounds) {
        return;
      }
      setSelectedStartTimestamp(bounds.start);
      setSelectedEndTimestamp(bounds.end);
    };
    t7 = [timeData];
    $[9] = timeData;
    $[10] = t6;
    $[11] = t7;
  } else {
    t6 = $[10];
    t7 = $[11];
  }
  useEffect(t6, t7);
  let t8;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = (start_0, end_0) => {
      setSelectedStartTimestamp(start_0);
      setSelectedEndTimestamp(end_0);
    };
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  const handleApplyRange = t8;
  let t9;
  if ($[13] !== selectedEndTimestamp || $[14] !== selectedStartTimestamp || $[15] !== timeData?.timeIntervals) {
    bb0: {
      if (!selectedStartTimestamp || !selectedEndTimestamp) {
        t9 = null;
        break bb0;
      }
      const selection = Interval.fromDateTimes(selectedStartTimestamp, selectedEndTimestamp);
      if (!isValidInterval(selection)) {
        t9 = null;
        break bb0;
      }
      t9 = clampToIntervals(selection, (timeData?.timeIntervals ?? []).filter(isValidInterval));
    }
    $[13] = selectedEndTimestamp;
    $[14] = selectedStartTimestamp;
    $[15] = timeData?.timeIntervals;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  const requestInterval = t9;
  const t10 = requestInterval?.start ?? null;
  const t11 = requestInterval?.end ?? null;
  let t12;
  if ($[17] !== activeView || $[18] !== displayedVehicleTypes || $[19] !== rangeValues || $[20] !== selectedMetric || $[21] !== t10 || $[22] !== t11 || $[23] !== viewMode) {
    t12 = <MapMain displayedVehicleTypes={displayedVehicleTypes} selectedMetric={selectedMetric} viewMode={viewMode} selectedStartTime={t10} selectedEndTime={t11} rangeValues={rangeValues} activeView={activeView} />;
    $[17] = activeView;
    $[18] = displayedVehicleTypes;
    $[19] = rangeValues;
    $[20] = selectedMetric;
    $[21] = t10;
    $[22] = t11;
    $[23] = viewMode;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] !== draftEndTimestamp || $[26] !== draftStartTimestamp || $[27] !== selectedEndTimestamp || $[28] !== selectedStartTimestamp) {
    t13 = selectedStartTimestamp && selectedEndTimestamp && <Box position="absolute" top="24" left="4" zIndex="4"><TimeIntervalDisplay startMinutes={dateToMinutes(draftStartTimestamp ?? selectedStartTimestamp)} endMinutes={dateToMinutes(draftEndTimestamp ?? selectedEndTimestamp)} dateKey={(draftStartTimestamp ?? selectedStartTimestamp).toISODate()} /></Box>;
    $[25] = draftEndTimestamp;
    $[26] = draftStartTimestamp;
    $[27] = selectedEndTimestamp;
    $[28] = selectedStartTimestamp;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  let t14;
  if ($[30] !== t12 || $[31] !== t13) {
    t14 = <Box flex="3" position="relative">{t12}{t13}</Box>;
    $[30] = t12;
    $[31] = t13;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  let t15;
  if ($[33] !== activeFilter || $[34] !== selectedEndTimestamp || $[35] !== selectedStartTimestamp || $[36] !== timeData?.timeIntervals) {
    t15 = activeFilter === FilterType.timesetPicker && <Box display="flex" justifyContent="center" mb="4"><TimeFilter timeIntervals={timeData?.timeIntervals ?? []} onApplyRange={handleApplyRange} onDraftChange={handleDraftChange} selectedStart={selectedStartTimestamp} selectedEnd={selectedEndTimestamp} /></Box>;
    $[33] = activeFilter;
    $[34] = selectedEndTimestamp;
    $[35] = selectedStartTimestamp;
    $[36] = timeData?.timeIntervals;
    $[37] = t15;
  } else {
    t15 = $[37];
  }
  let t16;
  if ($[38] !== activeFilter || $[39] !== displayedVehicleTypes || $[40] !== vehicleTypesData?.vehicleTypes) {
    t16 = activeFilter === FilterType.vehicleTypes && <Box display="flex" justifyContent="center" mb="4"><VehicleTypeFilter selectedVehicleTypes={displayedVehicleTypes} setSelectedVehicleTypes={setDisplayedVehicleTypes} availableVehicleTypes={vehicleTypesData?.vehicleTypes} /></Box>;
    $[38] = activeFilter;
    $[39] = displayedVehicleTypes;
    $[40] = vehicleTypesData?.vehicleTypes;
    $[41] = t16;
  } else {
    t16 = $[41];
  }
  let t17;
  if ($[42] !== activeFilter || $[43] !== datasetRanges || $[44] !== rangeValues || $[45] !== selectedMetric || $[46] !== viewMode) {
    t17 = activeFilter === FilterType.metrics && <Box display="flex" justifyContent="center" mb="4"><MetricFilter selectedMetric={selectedMetric} onMetricChange={metric_0 => {
        setSelectedMetric(metric_0);
        const avail = METRIC_VIEW_AVAILABILITY[metric_0];
        if (!avail[viewMode]) {
          setViewMode(getDefaultViewMode(metric_0));
        }
      }} viewMode={viewMode} onViewModeChange={setViewMode} rangeValues={rangeValues} onRangeChange={handleRangeChange} datasetRanges={datasetRanges} /></Box>;
    $[42] = activeFilter;
    $[43] = datasetRanges;
    $[44] = rangeValues;
    $[45] = selectedMetric;
    $[46] = viewMode;
    $[47] = t17;
  } else {
    t17 = $[47];
  }
  let t18;
  if ($[48] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = view => {
      setActiveView(view);
      setActiveFilter(prev_0 => prev_0 !== null && !filtersByView[view].includes(prev_0) ? null : prev_0);
    };
    $[48] = t18;
  } else {
    t18 = $[48];
  }
  let t19;
  if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = filter => {
      setActiveFilter(prev_1 => prev_1 === filter ? null : filter);
    };
    $[49] = t19;
  } else {
    t19 = $[49];
  }
  let t20;
  if ($[50] !== activeFilter || $[51] !== activeView) {
    t20 = <Menu activeView={activeView} onViewChange={t18} activeFilter={activeFilter} onFilterChange={t19} />;
    $[50] = activeFilter;
    $[51] = activeView;
    $[52] = t20;
  } else {
    t20 = $[52];
  }
  let t21;
  if ($[53] !== t15 || $[54] !== t16 || $[55] !== t17 || $[56] !== t20) {
    t21 = <Box position="absolute" bottom="0" left="0" right="0" zIndex="5">{t15}{t16}{t17}{t20}</Box>;
    $[53] = t15;
    $[54] = t16;
    $[55] = t17;
    $[56] = t20;
    $[57] = t21;
  } else {
    t21 = $[57];
  }
  let t22;
  if ($[58] !== t14 || $[59] !== t21) {
    t22 = <MainLayout>{t14}{t21}</MainLayout>;
    $[58] = t14;
    $[59] = t21;
    $[60] = t22;
  } else {
    t22 = $[60];
  }
  return t22;
};
export default BaseMap;